/**
 * Auth store — Zustand.
 * После логина хранит accessToken и account.
 *
 * Phase 2.6 (Session/Auth UX):
 * - `expired` flag — выставляется в true при 401 от любого API-запроса.
 *   Используется <Shell> для показа <AuthExpiredModal> без полного redirect.
 * - При setAuth(expired) сбрасывается обратно в false.
 * - logout() тоже сбрасывает expired (на случай если logout нажат из модалки).
 *
 * Invariants:
 * - expired=true требует accessToken=null (выставляется в markExpired/clearAuth).
 * - api.ts interceptor вызывает markExpired, но НЕ делает redirect — это
 *   <AuthExpiredModal> в Shell берёт на себя UX.
 */
import { create } from 'zustand';

import type { Account } from '@/hooks/useAccount';

interface AuthState {
  account: Account | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  /** True если последний API-ответ был 401 — показываем AuthExpiredModal. */
  expired: boolean;

  setAuth: (account: Account, accessToken: string) => void;
  setAccount: (account: Account | null) => void;
  logout: () => void;
  /** api.ts 401-handler: чистим token + помечаем expired. */
  markExpired: () => void;
  /** Вызывается после успешного login из AuthExpiredModal / LoginPage. */
  clearExpired: () => void;
}

const TOKEN_KEY = 'cs_access_token';
const ACCOUNT_KEY = 'cs_account';

function readPersisted(): { account: Account | null; token: string | null } {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const accStr = localStorage.getItem(ACCOUNT_KEY);
    const account = accStr ? (JSON.parse(accStr) as Account) : null;
    return { account, token };
  } catch {
    return { account: null, token: null };
  }
}

const initial = readPersisted();

export const useAuthStore = create<AuthState>((set) => ({
  account: initial.account,
  accessToken: initial.token,
  isAuthenticated: !!initial.token,
  expired: false,

  setAuth: (account, accessToken) => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
    set({ account, accessToken, isAuthenticated: true, expired: false });
  },

  setAccount: (account) => {
    if (account) {
      localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
    } else {
      localStorage.removeItem(ACCOUNT_KEY);
    }
    set({ account });
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
    set({
      account: null,
      accessToken: null,
      isAuthenticated: false,
      expired: false,
    });
  },

  markExpired: () => {
    // token НЕ удаляем сразу — AuthExpiredModal может попытаться сделать
    // retry login через существующий refresh-flow, а потом уже зачистим.
    // НО: следующий запрос с этим токеном снова даст 401, что безопасно.
    // Решение: удаляем сразу, чтобы AuthGuard на следующем рендере знал что
    // нужно логиниться (если модалка закроется без логина — Navigate /login).
    set({
      accessToken: null,
      isAuthenticated: false,
      expired: true,
    });
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },

  clearExpired: () => {
    set({ expired: false });
  },
}));
